/**
 * Create Agent Page — Full-page wizard (replaces CreateCustomBotModal)
 *
 * 3-step guided flow with sidebar navigation:
 *   Step 1: Essentials (name, description, AI model, template, prompt & style)
 *   Step 2: Metadata & Variables
 *   Step 3: Advanced Settings (search, temperature, handoff)
 *
 * Layout: Left step sidebar (w-60) + main content area (flex-1),
 * matching the AgentSettingsPage pattern.
 */

import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  ArrowRight,
  Rocket,
  Settings,
  Zap,
  Target,
  Bot,
  Briefcase,
  Languages,
  Code2,
  Heart,
  Pencil,
  Loader2,
  Check,
  Sparkles,
  FileText,
  Variable,
} from 'lucide-react'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Textarea } from '../../components/ui/Textarea'
import { Select } from '../../components/ui/Select'
import { Separator } from '../../components/ui/Separator'
import { Switch } from '../../components/ui/switch'
import { ModelSelector } from '../../components/ui/ModelSelector'
import { useChatbotStore } from '../../stores/chatbotStore'
import { VariablesDisplay } from '../../components/features/chatbot/VariablesDisplay'
import { VariablesSection } from '../../components/features/chatbot/VariablesSection'
import { MetadataConfig } from '../../components/features/chatbot/MetadataConfig'
import {
  TEMPLATE_OPTIONS,
  TEMPLATE_TEXTS,
  DEFAULT_STYLE_TONE,
  SEARCH_THRESHOLD_OPTIONS,
  TEMPERATURE_OPTIONS,
  FALLBACK_STRATEGY_OPTIONS,

} from '../../components/chatbot/behaviorTemplates'
import type { CreateChatbotRequest } from '../../types'
import api from '../../services/api'
import { getUserPlans } from '../../services/plansApi'
import { useToast } from '../../hooks/useToast'

/* ── Icon map for behavior templates ──────────────────────── */
const ICON_MAP: Record<string, React.FC<{ className?: string }>> = {
  Target, Bot, Briefcase, Languages, Code2, Heart, Pencil,
}

/* ── Step definitions ─────────────────────────────────────── */
const STEPS = [
  {
    id: 1,
    label: 'Essentials',
    description: 'Name, model, template & prompt',
    icon: FileText,
  },
  {
    id: 2,
    label: 'Metadata & Variables',
    description: 'Fields & personalization',
    icon: Variable,
  },
  {
    id: 3,
    label: 'Advanced Settings',
    description: 'Search, temperature & handoff',
    icon: Settings,
  },
]

/* ── Page component ───────────────────────────────────────── */
export const CreateAgentPage: React.FC = () => {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { createChatbot, isCreating } = useChatbotStore()

  const [currentStep, setCurrentStep] = useState(1)
  const totalSteps = 3

  const [formData, setFormData] = useState<Partial<CreateChatbotRequest>>({
    bot_type: 'custom',
    name: '',
    description: '',
    ai_model: 'openai/gpt-4o-mini',
    tone: 'professional',
    personality: 'helpful',
    response_style: 'detailed',
    technical_level: 'beginner',
    search_threshold: 0.5,
    temperature: 0.1,
    fallback_strategy: 'clarify_first',
    allow_handoff: false,
    prompt_template_key: 'base_instructions',
    prompt_mode: 'append',
    owner_prompt: '',
    settings: { agent_runtime: 'pydanticai', style_tone_instructions: DEFAULT_STYLE_TONE },
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [availableModels, setAvailableModels] = useState<any[]>([])
  const [loadingModels, setLoadingModels] = useState(false)

  /* ── Plan limit check on mount ────────────────────────── */
  useEffect(() => {
    let mounted = true
    const checkLimit = async () => {
      try {
        const res = await getUserPlans()
        const planId = res.current_subscription?.plan_id
        const planName = res.current_subscription?.plan_name
        const plans = Array.isArray(res.available_plans) ? res.available_plans : []
        const currentPlan =
          plans.find((p: any) => (planId && p.id === planId) || (planName && p.name === planName)) ||
          plans.find(
            (p: any) =>
              p.name?.toLowerCase() === planName?.toLowerCase() ||
              p.display_name?.toLowerCase() === planName?.toLowerCase()
          )
        const limit = currentPlan?.chatbots_limit
        if (!mounted) return
        if (typeof limit === 'number' && limit >= 0) {
          const { chatbots } = useChatbotStore.getState()
          if (chatbots.length >= limit) {
            toast({
              title: 'Agent limit reached',
              description: 'Upgrade your plan to create more agents.',
              variant: 'warning',
              duration: 6000,
            })
            navigate('/dashboard/plans', { replace: true })
          }
        }
      } catch {
        // non-blocking — let the user continue
      }
    }
    checkLimit()
    return () => { mounted = false }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Load available models ────────────────────────────── */
  useEffect(() => {
    const loadModels = async () => {
      setLoadingModels(true)
      try {
        const response = await api.get('/llm-models/active')
        setAvailableModels(response.data.models || [])
      } catch {
        setAvailableModels([
          { value: 'openai/gpt-4o-mini', label: 'OPENAI - gpt-4o-mini', credit_cost: 10 },
          { value: 'openai/gpt-4o', label: 'OPENAI - gpt-4o', credit_cost: 50 },
        ])
      } finally {
        setLoadingModels(false)
      }
    }
    loadModels()
  }, [])

  /* ── Set default owner prompt from template ───────────── */
  useEffect(() => {
    const key = formData.prompt_template_key || 'base_instructions'
    if (!formData.owner_prompt && key !== 'custom') {
      setFormData(prev => ({ ...prev, owner_prompt: TEMPLATE_TEXTS[key] || '' }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /* ── Helpers ──────────────────────────────────────────── */
  const updateFormData = useCallback((updates: Partial<CreateChatbotRequest>) => {
    setFormData(prev => ({ ...prev, ...updates }))
    setErrors(prev => {
      const next = { ...prev }
      Object.keys(updates).forEach(key => delete next[key])
      return next
    })
  }, [])

  const validateStep = (step: number) => {
    const e: Record<string, string> = {}
    if (step === 1 && !formData.name?.trim()) e.name = 'Agent name is required'
    if (step === 3 && formData.owner_prompt && formData.owner_prompt.length > 10000)
      e.owner_prompt = 'Custom prompt must not exceed 10,000 characters'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleNext = () => {
    if (validateStep(currentStep)) setCurrentStep(s => Math.min(s + 1, totalSteps))
  }

  const handlePrevious = () => setCurrentStep(s => Math.max(s - 1, 1))

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return
    try {
      const payload: CreateChatbotRequest = {
        name: formData.name!,
        description: formData.description || '',
        bot_type: 'custom',
        ai_model: formData.ai_model || 'openai/gpt-4o-mini',
        industry: '', company_size: '', primary_service: '', target_audience: '',
        support_topics: [], custom_topics: '',
        tone: formData.tone || 'professional',
        personality: formData.personality || 'helpful',
        response_style: formData.response_style || 'detailed',
        technical_level: formData.technical_level || 'beginner',
        search_threshold: formData.search_threshold ?? 0.5,
        temperature: formData.temperature ?? 0.1,
        fallback_strategy: formData.fallback_strategy || 'clarify_first',
        allow_handoff: formData.allow_handoff ?? false,
        avoid_topics: '',
        prompt_template_key: formData.prompt_template_key,
        owner_prompt: formData.owner_prompt,
        prompt_mode: formData.prompt_mode as any,
        settings: formData.settings,
        metadata_schema: formData.metadata_schema,
      }
      const bot = await createChatbot(payload)
      navigate(`/dashboard/chatbots/${bot.id}`, { replace: true })
    } catch (error) {
      setErrors({ submit: error instanceof Error ? error.message : 'Failed to create agent' })
    }
  }

  const handleCancel = () => {
    if (isCreating) return
    navigate('/dashboard/chatbots')
  }

  const handleTemplateSelect = (key: string) => {
    if (key === 'custom') {
      updateFormData({ prompt_template_key: key, owner_prompt: '' })
    } else {
      updateFormData({ prompt_template_key: key, owner_prompt: TEMPLATE_TEXTS[key] || '' })
    }
  }

  const promptLen = formData.owner_prompt?.length || 0
  const promptPct = promptLen / 10000

  /* ── Step 1: Essentials ───────────────────────────────── */
  const renderStep1 = () => (
    <div className="space-y-5">
      {/* Name */}
      <div>
        <label className="text-sm font-medium text-foreground mb-1.5 block">
          Agent Name <span className="text-destructive">*</span>
        </label>
        <Input
          placeholder="e.g., Product Support Agent"
          value={formData.name || ''}
          onChange={e => updateFormData({ name: e.target.value })}
          error={errors.name}
          className="h-10"
        />
      </div>

      {/* Description */}
      <div>
        <label className="text-sm font-medium text-foreground mb-1.5 block">Description</label>
        <Textarea
          placeholder="What does this agent do?"
          value={formData.description || ''}
          onChange={e => updateFormData({ description: e.target.value })}
          rows={2}
        />
        <p className="mt-1.5 text-xs text-muted-foreground">
          This shapes your AI's identity — it will introduce itself based on this.
        </p>
      </div>

      {/* AI Model */}
      <div>
        <label className="text-sm font-medium text-foreground mb-1.5 flex items-center gap-1.5">
          <Zap className="w-3.5 h-3.5 text-amber-500" />
          AI Model
        </label>
        <ModelSelector
          models={availableModels}
          value={formData.ai_model || 'openai/gpt-4o-mini'}
          onChange={v => {
            updateFormData({ ai_model: v })
            // Kimi models only accept temperature=1.0
            if (v.startsWith('moonshotai/')) updateFormData({ temperature: 1.0 })
          }}
          disabled={loadingModels}
          loading={loadingModels}
        />
      </div>

      <Separator />

      {/* Template Picker */}
      <div>
        <label className="text-sm font-medium text-foreground mb-3 block">
          Behavior Template
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
          {TEMPLATE_OPTIONS.map(tpl => {
            const IconComp = ICON_MAP[tpl.icon]
            const selected = (formData.prompt_template_key || 'base_instructions') === tpl.key
            return (
              <button
                key={tpl.key}
                type="button"
                onClick={() => handleTemplateSelect(tpl.key)}
                className={`
                  relative text-left p-3.5 rounded-xl transition-all duration-150
                  ${selected
                    ? 'border-2 border-primary bg-primary/[0.06] shadow-sm'
                    : 'border border-border bg-card hover:border-muted-foreground/30 hover:shadow-sm'
                  }
                `}
              >
                {selected && (
                  <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                    <Check className="w-3 h-3 text-primary-foreground" />
                  </div>
                )}
                {IconComp && (
                  <IconComp className={`w-5 h-5 mb-2 ${selected ? 'text-primary' : 'text-muted-foreground'}`} />
                )}
                <div className="text-sm font-medium text-foreground leading-tight">{tpl.name}</div>
                <div className="text-xs text-muted-foreground mt-0.5 leading-snug line-clamp-2">{tpl.description}</div>
              </button>
            )
          })}
        </div>
      </div>

      <Separator />

      {/* Owner Prompt */}
      <div>
        <label className="text-sm font-medium text-foreground mb-1.5 block">
          Owner Prompt
        </label>
        <VariablesDisplay formData={formData} />
        <textarea
          className={`
            w-full min-h-[160px] px-3.5 py-2.5 border rounded-xl text-sm leading-relaxed
            focus:outline-none focus:ring-2 transition-colors bg-background text-foreground
            ${promptPct >= 0.99
              ? 'border-destructive focus:ring-destructive bg-destructive/5'
              : promptPct >= 0.95
              ? 'border-amber-400 focus:ring-amber-500 bg-amber-500/5'
              : 'border-input focus:ring-ring focus:border-ring'
            }
          `}
          placeholder="Write your agent's instructions here..."
          value={formData.owner_prompt || ''}
          onChange={e => {
            const val = e.target.value
            if (val.length > 10000) return
            const key = formData.prompt_template_key || 'base_instructions'
            if (key !== 'custom') {
              updateFormData({ owner_prompt: val, prompt_template_key: 'custom' })
            } else {
              updateFormData({ owner_prompt: val })
            }
          }}
          maxLength={10000}
        />
        <div className="flex justify-between mt-1.5">
          <p className="text-xs text-muted-foreground">Cannot override system safety rules.</p>
          <span className={`text-xs font-medium tabular-nums ${
            promptPct >= 0.95 ? 'text-destructive' : promptPct >= 0.8 ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'
          }`}>
            {promptLen.toLocaleString()}/10,000
          </span>
        </div>
      </div>

      {/* Style & Tone */}
      <div>
        <label className="text-sm font-medium text-foreground mb-1.5 block">
          Style & Tone
        </label>
        <textarea
          className="w-full min-h-[80px] px-3.5 py-2.5 border border-input rounded-xl text-sm leading-relaxed bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
          placeholder="Define communication style..."
          value={(formData.settings as any)?.style_tone_instructions ?? DEFAULT_STYLE_TONE}
          onChange={e => updateFormData({ settings: { ...formData.settings, style_tone_instructions: e.target.value } })}
          maxLength={2000}
        />
        <div className="flex justify-between mt-1.5">
          <p className="text-xs text-muted-foreground">Injected into the system prompt.</p>
          <span className="text-xs text-muted-foreground tabular-nums">
            {((formData.settings as any)?.style_tone_instructions ?? DEFAULT_STYLE_TONE).length}/2,000
          </span>
        </div>
      </div>
    </div>
  )

  /* ── Step 2: Metadata & Variables ─────────────────────── */
  const renderStep2 = () => (
    <div className="space-y-8">
      <MetadataConfig
        value={formData.metadata_schema || { fields: [] }}
        onChange={schema => updateFormData({ metadata_schema: schema })}
      />
      <Separator />
      <VariablesSection formData={formData} onChange={updateFormData} />
    </div>
  )

  /* ── Step 3: Advanced Settings ────────────────────────── */
  const renderStep3 = () => (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium text-muted-foreground mb-1.5 block">Search Sensitivity</label>
          <Select
            options={SEARCH_THRESHOLD_OPTIONS}
            value={String(formData.search_threshold ?? 0.5)}
            onChange={v => updateFormData({ search_threshold: parseFloat(v) })}
          />
          <p className="mt-1 text-xs text-muted-foreground">Lower = more results</p>
        </div>

        <div>
          <label className="text-sm font-medium text-muted-foreground mb-1.5 block">AI Temperature</label>
          <Select
            options={TEMPERATURE_OPTIONS}
            value={String(formData.temperature ?? 0.1)}
            onChange={v => updateFormData({ temperature: parseFloat(v) })}
            disabled={formData.ai_model?.startsWith('moonshotai/')}
          />
          {formData.ai_model?.startsWith('moonshotai/') ? (
            <p className="mt-1 text-xs text-muted-foreground">Kimi models require temperature 1.0</p>
          ) : (
            <p className="mt-1 text-xs text-muted-foreground">Lower = consistent</p>
          )}
        </div>

        <div className="flex items-center justify-between md:col-span-2 p-3 rounded-lg border border-border bg-muted/50">
          <div>
            <label className="text-sm font-medium text-foreground">Allow Human Handoff</label>
            <p className="text-xs text-muted-foreground mt-0.5">Agent can transfer to a live agent.</p>
          </div>
          <Switch
            checked={formData.allow_handoff ?? false}
            onCheckedChange={checked => updateFormData({ allow_handoff: checked })}
          />
        </div>

        {/* Thinking mode indicator */}
        {(() => {
          const selected = availableModels.find(m => m.value === formData.ai_model)
          return selected?.supports_thinking ? (
            <div className="flex items-center gap-2 px-3 py-2.5 md:col-span-2 bg-amber-500/10 border border-amber-500/20 rounded-lg">
              <Sparkles className="w-4 h-4 text-amber-500 shrink-0" />
              <p className="text-sm text-amber-800 dark:text-amber-200">
                This model supports <span className="font-medium">extended thinking</span>.
              </p>
            </div>
          ) : null
        })()}

        <div>
          <label className="text-sm font-medium text-muted-foreground mb-1.5 block">When No Answer Found</label>
          <Select
            options={FALLBACK_STRATEGY_OPTIONS}
            value={formData.fallback_strategy || 'clarify_first'}
            onChange={v => updateFormData({ fallback_strategy: v })}
            disabled={formData.allow_handoff === false}
          />
        </div>
      </div>
    </div>
  )

  /* ── Render ───────────────────────────────────────────── */
  const currentStepDef = STEPS.find(s => s.id === currentStep)!

  return (
    <div className="max-w-6xl">
      <div className="flex gap-8">
        {/* ── Desktop sidebar ────────────────────────────── */}
        <nav className="w-60 shrink-0 hidden lg:block">
          <div className="sticky top-6 space-y-1.5">
            {/* Back link */}
            <button
              onClick={handleCancel}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors mb-3"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to Agents
            </button>
            <div className="mb-4">
              <h1 className="text-lg font-semibold text-foreground">Create Agent</h1>
            </div>
            {STEPS.map((step) => {
              const done = currentStep > step.id
              const active = currentStep === step.id
              const StepIcon = step.icon

              return (
                <button
                  key={step.id}
                  type="button"
                  onClick={() => { if (done) setCurrentStep(step.id) }}
                  disabled={!done && !active}
                  className={`
                    group w-full flex items-center gap-3 px-3.5 py-3 rounded-xl text-left transition-all duration-150
                    ${active
                      ? 'bg-primary/[0.08] text-foreground shadow-sm ring-1 ring-primary/20'
                      : done
                      ? 'text-muted-foreground hover:bg-muted/60 hover:text-foreground cursor-pointer'
                      : 'text-muted-foreground/50 cursor-default'
                    }
                  `}
                >
                  {/* Step icon container */}
                  <div className={`
                    flex items-center justify-center w-8 h-8 rounded-lg shrink-0 transition-colors duration-150
                    ${active
                      ? 'bg-primary text-primary-foreground'
                      : done
                      ? 'bg-muted/80 text-muted-foreground group-hover:bg-muted'
                      : 'bg-muted/40 text-muted-foreground/50'
                    }
                  `}>
                    {done ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <StepIcon className="w-4 h-4" />
                    )}
                  </div>

                  {/* Label + description */}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium leading-tight truncate">{step.label}</div>
                    <div className="text-xs text-muted-foreground leading-snug mt-0.5 truncate">{step.description}</div>
                  </div>
                </button>
              )
            })}
          </div>
        </nav>

        {/* ── Mobile pills ───────────────────────────────── */}
        <div className="lg:hidden w-full mb-4" style={{ display: 'contents' }}>
          {/* rendered outside flex on mobile — see below */}
        </div>

        {/* ── Main content area ──────────────────────────── */}
        <div className="flex-1 min-w-0">
          {/* Mobile step pills (visible < lg) */}
          <div className="lg:hidden flex gap-1 overflow-x-auto pb-4 -mx-1 px-1">
            {STEPS.map((step) => {
              const done = currentStep > step.id
              const active = currentStep === step.id
              return (
                <button
                  key={step.id}
                  type="button"
                  onClick={() => { if (done) setCurrentStep(step.id) }}
                  disabled={!done && !active}
                  className={`
                    flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap shrink-0 transition-colors
                    ${active
                      ? 'bg-primary text-primary-foreground'
                      : done
                      ? 'bg-muted text-muted-foreground hover:text-foreground cursor-pointer'
                      : 'bg-muted/50 text-muted-foreground/50 cursor-default'
                    }
                  `}
                >
                  {done ? <Check className="w-3.5 h-3.5" /> : <step.icon className="w-3.5 h-3.5" />}
                  {step.label}
                </button>
              )
            })}
          </div>

          {/* Step content */}
          <div className="min-h-[400px]">
            {currentStep === 1 ? renderStep1() : currentStep === 2 ? renderStep2() : renderStep3()}
          </div>

          {/* Error */}
          {errors.submit && (
            <div className="mt-6 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
              <p className="text-sm text-destructive">{errors.submit}</p>
            </div>
          )}

          {/* Footer navigation */}
          <div className="mt-8 pt-6 border-t border-border flex items-center justify-between">
            {/* Left: Back (step navigation) */}
            {currentStep > 1 ? (
              <Button variant="ghost" onClick={handlePrevious} disabled={isCreating}>
                <ArrowLeft className="w-4 h-4 mr-1.5" />
                Back
              </Button>
            ) : (
              <div />
            )}

            {/* Center: Step dots */}
            <div className="flex items-center gap-1.5">
              {STEPS.map((step) => (
                <div
                  key={step.id}
                  className={`w-1.5 h-1.5 rounded-full transition-colors ${
                    currentStep === step.id ? 'bg-primary' : 'bg-muted-foreground/30'
                  }`}
                />
              ))}
            </div>

            {/* Right: Next / Create */}
            {currentStep < totalSteps ? (
              <Button onClick={handleNext} disabled={isCreating}>
                Next
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={isCreating}>
                {isCreating ? (
                  <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Creating...</>
                ) : (
                  <><Rocket className="w-4 h-4 mr-1.5" />Create Agent</>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default CreateAgentPage
